import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Alert,
  Button,
  ErrorAlert,
  Field,
  Input,
  Logo,
  buttonClass,
  cx,
  useDocumentTitle,
} from "@unizpay/ui";
import { MailCheck } from "lucide-react";
import { type FormEvent, type ReactNode, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { api } from "../lib/api";

/** Centered card on the gray canvas, shared by sign-in and the sign-in callback. */
export function AuthShell({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center px-4 py-10">
      <Logo className="mb-8 h-9" />
      <main className="w-full max-w-sm rounded-lg border border-line bg-surface px-6 py-7 shadow-sm">
        {children}
      </main>
      <p className="mt-6 max-w-sm text-center text-xs text-fg-muted">
        Crypto payments, paid straight to your wallet.
      </p>
    </div>
  );
}

/** What went wrong on the way back from Google (`?error=` from /auth/google/callback). */
const GOOGLE_ERRORS: Record<string, string> = {
  google_cancelled: "Google sign-in was cancelled. Try again, or use your email.",
  google_failed: "Google sign-in didn't go through. Try again, or use your email.",
  google_unavailable: "Google sign-in isn't available right now. Use your email instead.",
};

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}

/**
 * Sign-in and sign-up are one flow (an emailed link, or Google; a new email creates the account).
 * The two entry points only differ in wording: /login for people coming back, /signup for "Start for free".
 */
const COPY = {
  signin: {
    docTitle: "Sign in",
    title: "Sign in to your account",
    lead: "We'll email you a sign-in link. No password needed.",
    switchText: "New to UnizPay?",
    switchLink: { to: "/signup", label: "Create an account" },
  },
  signup: {
    docTitle: "Create your account",
    title: "Create your account",
    lead: "Free to start. No password needed: we'll email you a sign-in link.",
    switchText: "Already have an account?",
    switchLink: { to: "/login", label: "Sign in" },
  },
} as const;

export function LoginPage({ mode = "signin" }: { mode?: keyof typeof COPY }) {
  const copy = COPY[mode];
  useDocumentTitle(copy.docTitle);
  const [email, setEmail] = useState("");
  const login = useMutation({ mutationFn: (e: string) => api.auth.login(e) });
  const options = useQuery({
    queryKey: ["auth", "options"],
    queryFn: api.auth.options,
    staleTime: Number.POSITIVE_INFINITY,
  });
  const [params] = useSearchParams();
  const googleError = GOOGLE_ERRORS[params.get("error") ?? ""];

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    login.mutate(email.trim());
  }

  if (login.isSuccess) {
    return (
      <AuthShell>
        <span className="flex size-10 items-center justify-center rounded-full bg-accent/10 text-accent">
          <MailCheck aria-hidden="true" className="size-5" />
        </span>
        <h1 className="mt-4 text-lg font-semibold">Check your email</h1>
        <p className="mt-1.5 text-[0.8125rem] text-fg-muted">
          We sent a sign-in link to <span className="font-medium text-fg">{login.variables}</span>. The link
          expires in 15 minutes.
        </p>
        <Button variant="secondary" size="lg" className="mt-6 w-full" onClick={() => login.reset()}>
          Use a different email
        </Button>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <h1 className="text-lg font-semibold">{copy.title}</h1>
      <p className="mt-1 text-[0.8125rem] text-fg-muted">{copy.lead}</p>
      {googleError && (
        <Alert tone="warn" className="mt-5">
          {googleError}
        </Alert>
      )}
      {options.data?.google && (
        <>
          {/* A full navigation: the API sends the browser on to Google. */}
          <a href="/auth/google" className={cx(buttonClass("secondary", "lg"), "mt-6 w-full")}>
            <GoogleIcon />
            Continue with Google
          </a>
          <div className="my-5 flex items-center gap-3 text-xs text-fg-subtle">
            <span className="h-px flex-1 bg-line" />
            or with your email
            <span className="h-px flex-1 bg-line" />
          </div>
        </>
      )}
      <form onSubmit={onSubmit} className={cx("space-y-4", !options.data?.google && "mt-6")}>
        <Field label="Email">
          {(p) => (
            <Input
              {...p}
              type="email"
              name="email"
              autoComplete="email"
              required
              maxLength={254}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
            />
          )}
        </Field>
        <ErrorAlert error={login.error} />
        <Button type="submit" size="lg" className="w-full" loading={login.isPending}>
          Email me a sign-in link
        </Button>
      </form>
      <p className="mt-6 text-center text-[0.8125rem] text-fg-muted">
        {copy.switchText}{" "}
        <Link to={copy.switchLink.to} className="font-medium text-accent hover:underline">
          {copy.switchLink.label}
        </Link>
      </p>
    </AuthShell>
  );
}
